import { useEffect, useState } from 'preact/hooks';
import { useSelector } from 'react-redux';
import { recirculationVideoDataSelector } from '../store/selectors/recirculationVideoData.selector';
import { debounce, isEmpty } from '../utils/utils';
import { anchorDataSelector } from '../store/selectors/anchorData.selector';

const DELAY_RESIZE = 300;

export interface TitleLine {
    text: Array<string>;
}

export interface UseResizeTitleValue {
  titleByLines: Array<TitleLine>;
  isFullTitleByLines: boolean;
}

const titleLineExceedsAvailableWidth = (titleStringArray: Array<string>, titleByLines: Array<TitleLine>, setTitleByLines: (newArray: Array<TitleLine>) => void): void => {
  const newTitleByLines = [...titleByLines];
  newTitleByLines[newTitleByLines.length - 1].text.pop();
  newTitleByLines.push({ text: [] });
  setTitleByLines(newTitleByLines);
};

export const useResizeStoriesTitle = (storiesTitleElement: Array<HTMLSpanElement>, titleWrapperElement: HTMLDivElement): UseResizeTitleValue => {
  const currentSceneTitle = useSelector(recirculationVideoDataSelector.currentSceneTitle);
  const anchorStatus = useSelector(anchorDataSelector.anchorStatus);

  const [titleByLines, setTitleByLines] = useState<Array<TitleLine>>([{ text: [] }]);
  const [titleByStrings, setTitleByStrings] = useState<Array<string>>([]);
  const [shouldUpdateTitleByStrings, setShouldUpdateTitleByStrings] = useState<boolean>(false);
  const [isResized, setResized] = useState<boolean>(false);

  useEffect(() => {
    const resizeHandler = () => setResized(true);
    window.addEventListener('resize', debounce(resizeHandler, DELAY_RESIZE));
    return (): void => {
      window.removeEventListener('resize', resizeHandler);
    };
  });

  useEffect(() => setResized(true), [anchorStatus]);

  useEffect(() => {
    if (!isEmpty(currentSceneTitle) || isResized) {
      setTitleByLines([{ text: [] }]);
      setTitleByStrings(currentSceneTitle.split(' '));
      setResized(false);
    }
  }, [currentSceneTitle, isResized, setTitleByLines]);

  useEffect(() => {
    if (titleByStrings.length > 0 && !shouldUpdateTitleByStrings) {
      const newNextString = titleByStrings[0];
      const newTitlesArray = [...titleByLines];
      newTitlesArray[newTitlesArray.length - 1].text.push(newNextString);
      setTitleByLines(newTitlesArray);
      setShouldUpdateTitleByStrings(true);
    }
  }, [titleByStrings, shouldUpdateTitleByStrings, titleByLines]);

  useEffect(() => {
    if (!isEmpty(storiesTitleElement) && titleWrapperElement && shouldUpdateTitleByStrings) {
      const currentLineWidth = storiesTitleElement[titleByLines.length - 1].clientWidth;
      if (currentLineWidth < titleWrapperElement.clientWidth - 12) {
        setTitleByStrings(titleStringArray => titleStringArray.slice(1, titleStringArray.length));
      } else {
        titleLineExceedsAvailableWidth(titleByStrings, titleByLines, setTitleByLines);
      }
      setShouldUpdateTitleByStrings(false);
    }
  }, [shouldUpdateTitleByStrings, titleWrapperElement, storiesTitleElement, titleByLines, titleByStrings]);

  return {
    titleByLines,
    isFullTitleByLines: isEmpty(titleByStrings),
  };
};
